import * as Sentry from '@sentry/react';
import type { ErrorEvent, EventHint } from '@sentry/react';
import { getFrontendEnv } from '@src/env';
import { isNetworkError } from '@src/util/networkError';
import {
    sampleRate,
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate
} from './sampleRate';

const env = getFrontendEnv().replace('7shifts_', '');

export const initializeSentry = () => {
    if (!shouldSendLogsToSentry()) {
        return;
    }

    Sentry.init({
        environment: env,
        dsn: 'https://c1b9969bffd2be38bdca2099c992c7cc@o4506282637066240.ingest.sentry.io/4506316762906624',
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: true,
                blockAllMedia: true
            })
        ],
        tracePropagationTargets: [
            /^https?:\/\/7shifts\.test/,
            /^https:\/\/[a-zA-Z0-9_-]+\.7shifts\.dev/,
            /^https:\/\/app.7shiftsdemo\.com/,
            /^https:\/\/app.7shifts\.com/,
            /^\//
        ],
        enabled: true,
        // This will send Stack Trace even on simple log (those that are logged without an error)
        attachStacktrace: true,
        // Logs
        sampleRate,
        // Traces / Performance units
        tracesSampleRate,
        // Session Replay
        replaysSessionSampleRate,
        replaysOnErrorSampleRate,
        beforeSend: (event: ErrorEvent, hint: EventHint) => {
            let processedEvent = handleUnhandledRejection(event, hint);
            if (!processedEvent) {
                return null;
            }
            processedEvent = handleNetworkError(processedEvent, hint);
            if (!processedEvent) {
                return null;
            }
            processedEvent = handle401Errors(processedEvent); //excluding session loss logouts until issue fixed, remove this to turn 401 logging back on
            if (!processedEvent) {
                return null;
            }
            processedEvent = handleForbiddenError(processedEvent, hint);
            return processedEvent;
        },
        _experiments: { enableMetrics: true }
    });
};

export const shouldSendLogsToSentry = (): boolean => {
    const SENTRY_ENVIRONMENTS = [
        // 'local', //uncomment this to enable Sentry logs locally
        // 'sandbox', //uncomment this to enable Sentry logs in sandboxes
        'demo',
        'prod'
    ];
    return SENTRY_ENVIRONMENTS.includes(env);
};

/*
 * Functions for Formatting Events Before Sending to Sentry
 */

const handleUnhandledRejection = (
    event: ErrorEvent,
    hint: EventHint | undefined
) => {
    if (
        hint &&
        hint.mechanism &&
        hint.mechanism.type === 'onunhandledrejection'
    ) {
        // Ignoring all unhandled promise rejections after talking with guild sentry bug hunters team
        return null;
    }
    return event;
};

/**
 * Drops client-side network/offline failures (device sleep/wake, dropped
 * connections, captive portals, etc.). These surface as `FetchError` from the
 * generated API client whenever `fetch` itself rejects and are not actionable,
 * so they should not create Sentry noise regardless of which fetch/page/thunk
 * triggered them.
 */
export const handleNetworkError = (
    event: ErrorEvent,
    hint: EventHint | undefined
) => {
    if (isNetworkError(hint?.originalException)) {
        return null;
    }
    return event;
};

const handleForbiddenError = (
    event: ErrorEvent,
    hint: EventHint | undefined
) => {
    const error = hint?.originalException as { message?: string } | undefined;
    if (error?.message?.includes('403')) {
        event.fingerprint = ['expected-permission-error'];
        event.level = 'info';
    }
    return event;
};

// temporary solution to exclude 401 logs until session token loss issue fixed
export const handle401Errors = (event: ErrorEvent) => {
    const breadcrumbs = event.breadcrumbs || [];

    const has401Breadcrumb = breadcrumbs?.some(
        breadcrumb => breadcrumb.data?.status_code === 401
    );
    if (has401Breadcrumb) {
        return null;
    }
    return event;
};
